/**
 * Admin Portal → Codes: every request the "Coach Cedar codes" and "Code batches" pages make
 * (`/api/admin/codes/*`, routers/admin_codes.py). Through the shared `adminFetch` /
 * `adminRequest` (web/src/utils/adminApi.ts): the absolute API origin, the admin session header,
 * no request without one. A refusal is a `PartnerApiError` with the server's own words and code
 * (the same error the partner pages read), so a house rule (409 `{code, message}`) shows as said.
 *
 * Web-only (the admin SPA).
 */

import { AdminApiError, adminFetch, adminRequest } from '../../../utils/adminApi';
import { NOT_JSON_MESSAGE, PartnerApiError } from '../../../services/partnersAdminService';
import { describeApiError, errorCode, errorDetail, type TagRow } from '../partners/partnersLogic';
import {
  CODES_API,
  batchesQuery,
  type BatchCode,
  type BatchFilters,
  type AdoptResult,
  type BatchRow,
  type CounterCheck,
  type HouseOverview,
} from './codesAdminLogic';
import {
  activityQuery,
  catalogQuery,
  rewardImportQuery,
  type ActivityPage,
  type ApiSummary,
  type CatalogFilters,
  type CatalogPage,
  type CatalogSummary,
  type CodeDetail,
  type InventorySummary,
  type OffersView,
  type RewardImportForm,
} from './codesAreaLogic';
import { RECONCILE_API, type ReconcileRuns } from './codesReconcileLogic';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let body: T;
  try {
    body = await adminFetch<T>(path, init);
  } catch (err) {
    if (err instanceof AdminApiError) {
      throw new PartnerApiError(err.status, describeApiError(err.status, err.body), errorCode(err.body), errorDetail(err.body));
    }
    if (err instanceof SyntaxError) throw new PartnerApiError(0, NOT_JSON_MESSAGE, null, null);
    throw err;
  }
  if (body == null || typeof body !== 'object') throw new PartnerApiError(0, NOT_JSON_MESSAGE, null, null);
  return body;
}

async function download(path: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> {
  let res: Response;
  try {
    res = await adminRequest(path);
  } catch (err) {
    if (err instanceof AdminApiError) {
      throw new PartnerApiError(err.status, describeApiError(err.status, err.body), errorCode(err.body), errorDetail(err.body));
    }
    throw err;
  }
  const type = res.headers.get('content-type') || '';
  if (type.includes('text/html')) throw new PartnerApiError(0, NOT_JSON_MESSAGE, null, null);
  const disposition = res.headers.get('content-disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: m ? m[1] : fallbackName };
}

const enc = encodeURIComponent;

export const CodesAdmin = {
  house: () => call<{ success: boolean } & HouseOverview>(`${CODES_API}/house`),
  createHouseTag: (body: Record<string, unknown>) =>
    call<{ tag: TagRow }>(`${CODES_API}/house/tags`, { method: 'POST', body: JSON.stringify(body) }),
  listBatches: (f: BatchFilters) =>
    call<{ batches: BatchRow[]; total: number; limit: number; offset: number }>(`${CODES_API}/batches${batchesQuery(f)}`),
  getBatch: (id: string) => call<{ batch: BatchRow }>(`${CODES_API}/batches/${enc(id)}`),
  batchCodes: (id: string, limit: number, offset: number) =>
    call<{ codes: BatchCode[]; total: number; limit: number; offset: number; codes_masked: boolean }>(
      `${CODES_API}/batches/${enc(id)}/codes?limit=${limit}&offset=${offset}`,
    ),
  exportBatch: (id: string) => download(`${CODES_API}/batches/${enc(id)}/export`, `code-batch-${String(id).slice(0, 8)}.csv`),
  counterCheck: () => call<{ success: boolean } & CounterCheck>(`${CODES_API}/counter-check`),
  /** super_admin only (audited): files every code with no batch the way migration 1877 would have. */
  adoptUnbatched: () => call<{ success: boolean } & AdoptResult>(`${CODES_API}/adopt-unbatched`, { method: 'POST' }),

  // ── the code read model (Codes PR-7; doc 38 §18.4) — the Codes area's screens (PR-8) ──
  catalog: (f: CatalogFilters, page: { limit: number; offset: number }, canPrefix: boolean) =>
    call<{ success: boolean } & CatalogPage>(`${CODES_API}/catalog${catalogQuery(f, page, canPrefix)}`),
  catalogSummary: () => call<{ success: boolean } & CatalogSummary>(`${CODES_API}/catalog/summary`),
  /** Admin roles only (403 otherwise); audited as a write. */
  exportCatalog: (f: CatalogFilters) => download(`${CODES_API}/catalog/export${catalogQuery(f)}`, 'code-catalog.csv'),
  code: (codeId: string) => call<{ success: boolean } & CodeDetail>(`${CODES_API}/catalog/codes/${enc(codeId)}`),
  activity: (kinds: string[], cursor: string | null, limit?: number) =>
    call<{ success: boolean } & ActivityPage>(`${CODES_API}/activity${activityQuery(kinds, cursor, limit)}`),
  apiSummary: (partnerId?: string) =>
    call<{ success: boolean } & ApiSummary>(`${CODES_API}/api-summary${partnerId ? `?partner_id=${enc(partnerId)}` : ''}`),
  inventory: () => call<{ success: boolean } & InventorySummary>(`${CODES_API}/inventory`),
  offers: () => call<{ success: boolean } & OffersView>(`${CODES_API}/offers`),

  // ── the nightly codes reconcile (Codes PR-9) ──
  reconcileRuns: () => call<{ success: boolean } & ReconcileRuns>(`${RECONCILE_API}/runs`),
  /** Admin roles only (403 otherwise; the server checks). 202 queued; 409 busy; 503 switched off. Audited. */
  runReconcile: () => call<{ success: boolean; status: string }>(`${RECONCILE_API}/run`, { method: 'POST' }),
  /** super_admin only: a Summer50 reward CSV from App Store Connect (POST /api/admin/growth/inventory/import).
   *  The file goes as multipart form data; the codes are never shown or logged. */
  importRewardCsv: (f: RewardImportForm, file: Blob) => {
    const form = new FormData();
    form.append('file', file, f.fileName || 'codes.csv');
    return call<{ imported: number; reward_sku: string }>(
      `/api/admin/growth/inventory/import${rewardImportQuery(f)}`, { method: 'POST', body: form },
    );
  },
};
